import { useEffect, useMemo, useState } from 'react'

import { createLocalAgentClient, transportRequest, type LocalAgentClient } from '@krp/client-runtime'

import type { OwnDispatch } from '../../../state'
import { OWN_AGENT_URLS } from '../api/urls'
import { createAgentSessionController, type AgentSessionActions, type AgentSessionModel } from './agentSessionController'
import { createTokenFetcher } from './agentToken'

export interface UseAgentSessionOptions {
  /** Store приложения: токен рабочего места берётся через его transport (cookie, CSRF, корреляция). */
  dispatch: OwnDispatch
  /** Какой уровень возможностей агента (`hello.api.level`) нужен окну; решает приложение. */
  minApiLevel: number
  /** Подставной клиент агента (тесты). */
  client?: LocalAgentClient
  siteOrigin?: string
}

export interface AgentSession {
  model: AgentSessionModel
  actions: AgentSessionActions
}

/**
 * Сессия продажи через кассовый агент для React: хук держит контроллер (`agentSessionController.ts`) пока окно открыто.
 * Токен рабочего места берётся у платформы через общий transport приложения. Контроллер один на жизнь окна.
 */
export function useAgentSession(options: UseAgentSessionOptions): AgentSession {
  const { dispatch, minApiLevel } = options
  const client = useMemo(() => options.client ?? createLocalAgentClient(), [options.client])
  const siteOrigin = options.siteOrigin ?? (typeof window === 'undefined' ? '' : window.location.origin)
  const [model, setModel] = useState<AgentSessionModel | null>(null)
  const controller = useMemo(
    () =>
      createAgentSessionController({
        client,
        siteOrigin,
        minApiLevel,
        fetchToken: createTokenFetcher((agentInstanceId) =>
          dispatch(transportRequest({ url: OWN_AGENT_URLS.token, method: 'POST', body: { agentInstanceId } })),
        ),
        onChange: setModel,
      }),
    [client, siteOrigin, minApiLevel, dispatch],
  )

  useEffect(() => {
    controller.start()
    return () => controller.stop()
  }, [controller])

  return { model: model ?? controller.getModel(), actions: controller.actions }
}
